// This is a COPY of downread/shared/src/utils/feed-filter.ts. To modify, edit the original and then run downread/bin/build-shared.sh
import { FeedFilter, FeedFilterKeyword, FeedFilterOp } from './api-schema';

/** The post fields a keyword filter can look at. Structural subset of feed-schema's Post
 *  (plus PostMeta's origlink) so callers can pass either. */
export interface FilterablePost {
    title?: string;
    /** HTML body/description; tags are stripped before matching. */
    description?: string;
    author?: string;
    link?: string;
    origlink?: string;
}

/** True when the filter has at least one usable keyword. */
export function isFilterActive(filter: FeedFilter | undefined): filter is FeedFilter {
    return !!filter && filter.keywords.some(k => k.text.trim());
}

/** Normalize a filter from user input: trims keywords, drops empty ones.
 *  Returns undefined when nothing usable remains (= filter off). */
export function normalizeFilter(match: 'any' | 'all', keywords: FeedFilterKeyword[]): FeedFilter | undefined {
    const cleaned = keywords
        .map(k => ({ text: k.text.trim(), field: k.field, ...(k.op && k.op != 'contains' ? { op: k.op } : {}) }))
        .filter(k => k.text);
    if (!cleaned.length) return undefined;
    return { match, keywords: cleaned };
}

// Replace tags with spaces and decode the entities common in feed HTML, so keywords never
// match inside markup (tag names, attributes, URLs in href/src). Whitespace is collapsed so
// startsWith/endsWith see the visible text, not formatting artifacts.
function stripHtml(html: string): string {
    return html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, (_, e) =>
            ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", nbsp: ' ' } as Record<string, string>)[e])
        .replace(/\s+/g, ' ')
        .trim();
}

// The not* operators invert the positive comparison across the keyword's whole field set,
// so e.g. notContains + 'all' means "no field contains the text".
function positiveOp(op: FeedFilterOp): { base: 'contains' | 'startsWith' | 'endsWith'; negated: boolean } {
    switch (op) {
        case 'notContains': return { base: 'contains', negated: true };
        case 'startsWith': return { base: 'startsWith', negated: false };
        case 'notStartsWith': return { base: 'startsWith', negated: true };
        case 'endsWith': return { base: 'endsWith', negated: false };
        case 'notEndsWith': return { base: 'endsWith', negated: true };
        default: return { base: 'contains', negated: false };
    }
}

function matchesKeyword(kw: FeedFilterKeyword, post: FilterablePost, bodyText: () => string): boolean {
    const text = kw.text.trim().toLowerCase();
    if (!text) return false;
    const { base, negated } = positiveOp(kw.op ?? 'contains');
    const cmp = (value: string | undefined) => {
        if (!value) return false;
        const v = value.toLowerCase().trim();
        return base == 'contains' ? v.includes(text) : base == 'startsWith' ? v.startsWith(text) : v.endsWith(text);
    };
    const inTitle = () => cmp(post.title);
    const inAuthor = () => cmp(post.author);
    const inUrl = () => cmp(post.link) || cmp(post.origlink);
    const inBody = () => cmp(bodyText());
    const positive = () => {
        switch (kw.field) {
            case 'title': return inTitle();
            case 'author': return inAuthor();
            case 'url': return inUrl();
            case 'body': return inBody();
            case 'all': return inTitle() || inAuthor() || inUrl() || inBody();
        }
    };
    return negated ? !positive() : positive();
}

/** True when the post matches the filter (and should be auto-marked read).
 *  'any' = at least one keyword matches; 'all' = every keyword matches.
 *  Keywords are case-insensitive substrings; blank keywords are ignored. */
export function matchesFilter(filter: FeedFilter, post: FilterablePost): boolean {
    const keywords = filter.keywords.filter(k => k.text.trim());
    if (!keywords.length) return false;
    // The body is stripped of HTML at most once per post, and only when a keyword needs it.
    let body: string | undefined;
    const bodyText = () => body ??= post.description ? stripHtml(post.description).toLowerCase() : '';
    const test = (kw: FeedFilterKeyword) => matchesKeyword(kw, post, bodyText);
    return filter.match == 'all' ? keywords.every(test) : keywords.some(test);
}
